import { useEffect, useSyncExternalStore } from "react";
import { CloseIcon } from "./Icons.tsx";
import { NoticeCoin } from "./Section.tsx";

/**
 * The confirmation that does not need reading twice.
 *
 * `SuccessNotice` stays on screen until dismissed, because "3 bookings moved and 3 members
 * notified" is worth checking. A toast is for the other kind of receipt: "Saved", "Copied",
 * "Package hidden". Bottom right at desktop, bottom centre on a phone, four seconds, and one at a
 * time: a second toast replaces the first rather than stacking, because two receipts at once is
 * one too many to read before they go.
 *
 * A module-level store, the same shape as the auth store, so a screen calls `showToast("Saved")`
 * from anywhere and the one `ToastViewport` the shell mounts paints it. Screens never mount their
 * own viewport.
 */

export const TOAST_DURATION_MS = 4000;

export type ToastTone = "success" | "info";

/** One short action inside the toast, such as "Undo". Tapping it runs the action and closes the toast. */
export type ToastAction = { label: string; onClick: () => void };

export type Toast = {
  id: number;
  message: string;
  tone: ToastTone;
  durationMs: number;
  action: ToastAction | null;
};

/** An action needs time to reach: a toast with "Undo" stays seven seconds. */
export const TOAST_ACTION_DURATION_MS = 7000;

let current: Toast | null = null;
let nextId = 1;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

export function showToast(
  message: string,
  options: { tone?: ToastTone; durationMs?: number; action?: ToastAction } = {},
): void {
  current = {
    id: nextId++,
    message,
    tone: options.tone ?? "success",
    durationMs: options.durationMs ?? (options.action ? TOAST_ACTION_DURATION_MS : TOAST_DURATION_MS),
    action: options.action ?? null,
  };
  emit();
}

export function dismissToast(): void {
  if (current === null) return;
  current = null;
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function read(): Toast | null {
  return current;
}

export function ToastViewport() {
  const toast = useSyncExternalStore(subscribe, read, read);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => {
      // Only the toast this timer was started for. A newer one has its own timer.
      if (current?.id === toast.id) dismissToast();
    }, toast.durationMs);
    return () => clearTimeout(timer);
  }, [toast]);

  if (!toast) return null;

  return (
    <div className="pointer-events-none fixed inset-x-4 bottom-4 z-[60] flex justify-center sm:inset-x-auto sm:right-6 sm:bottom-6 sm:justify-end">
      <output
        key={toast.id}
        className={[
          // Raised like every message box, with a deeper shadow because it floats over the page.
          "notice notice-float pointer-events-auto flex max-w-[420px] items-center gap-2.5 rounded-xl px-3.5 py-3 text-[14px] font-semibold motion-safe:animate-sheet",
          `notice-${toast.tone}`,
        ].join(" ")}
      >
        <NoticeCoin tone={toast.tone} />
        <span className="min-w-0 flex-1">{toast.message}</span>
        {toast.action ? (
          <button
            type="button"
            onClick={() => {
              const action = toast.action;
              dismissToast();
              action?.onClick();
            }}
            className="min-h-9 shrink-0 rounded-lg px-2.5 text-[14px] font-bold underline underline-offset-2"
          >
            {toast.action.label}
          </button>
        ) : null}
        <button
          type="button"
          onClick={dismissToast}
          aria-label="Dismiss"
          className="-mr-1 flex size-7 shrink-0 items-center justify-center rounded-full opacity-70 transition-opacity hover:opacity-100"
        >
          <CloseIcon size={14} />
        </button>
      </output>
    </div>
  );
}
